import { Archive, ArchiveRestore, CheckCheck, Copy, Pencil, Pin, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useSearchParams } from 'react-router';
import { ANNOUNCEMENT_PRIORITIES, ANNOUNCEMENT_TYPES, typeDefaults, type Audience, type AnnouncementPriority, type AnnouncementType, type Recurrence } from '@pz/core';
import type { ProcOutput } from '@pz/server';
import { PRIORITY_TONE, AnnouncementCard, TypePill } from '../components/announcements';
import { Button, Checkbox, EmptyState, ErrorBox, Field, Input, Loading, Modal, Notice, PageHeader, Panel, Pill, Select, Tabs, Textarea, useToast } from '../components/ui';
import { d } from '../lib/format';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { useMe } from '../lib/me';
import { useSticky } from '../lib/sticky';

type ManageRow = ProcOutput<'announcements.manage'>['announcements'][number];
type Tab = 'current' | 'manage';

/**
 * Announcements: everybody reads what is aimed at them; people with the right "Mitteilungen" (set by management)
 * publish, change and archive, and see how many people have read an announcement.
 */
export function AnnouncementsPage() {
  const { t } = useTranslation();
  const { can } = useMe();
  const mine = useQ('announcements.mine', {});
  const canPublish = can('announcements.manage');
  const [tab, setTab] = useTabParam<Tab>(canPublish ? ['manage', 'current'] : ['current']);
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('ann.title')} subtitle={t('ann.subtitle')} />
      {canPublish && (
        <Tabs
          tabs={[
            { id: 'manage' as const, label: t('ann.tabManage') },
            { id: 'current' as const, label: t('ann.tabCurrent') },
          ]}
          value={tab}
          onChange={setTab}
        />
      )}
      {tab === 'current' ? <CurrentList q={mine} /> : <ManageList />}
    </div>
  );
}

function CurrentList({ q }: { q: ReturnType<typeof useQ<'announcements.mine'>> }) {
  const { t } = useTranslation();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const list = q.data?.announcements ?? [];
  if (!list.length) return <EmptyState>{t('ann.emptyCurrent')}</EmptyState>;
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
      {list.map((a) => (
        <AnnouncementCard key={`${a.id}:${a.occurrence}`} a={a} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Publisher's list                                                     */
/* ------------------------------------------------------------------ */

function statusOf(r: ManageRow, today: string): 'archived' | 'active' | 'planned' | 'expired' {
  if (r.archived) return 'archived';
  if (r.active) return 'active';
  return r.startsOn > today ? 'planned' : 'expired';
}

const STATUS_TONE = { active: 'good', planned: 'info', expired: 'neutral', archived: 'neutral' } as const;

function ManageList() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  // current, all, or only the archive (item 19: the archive alone, without the current ones)
  const [archived, setArchived] = useSticky<'active' | 'all' | 'only'>('announcements.archivedMode', 'active');
  const q = useQ('announcements.manage', { includeArchived: archived !== 'active', onlyArchived: archived === 'only' });
  const archive = useM('announcements.archive', { invalidate: ['announcements.', 'home.', 'notifications.'] });
  const [editing, setEditing] = useState<Form | null>(null);
  const [receiptsFor, setReceiptsFor] = useState<ManageRow | null>(null);

  // the start page links here with ?new=1 ("Mitteilung veröffentlichen")
  useEffect(() => {
    if (params.get('new') === '1') {
      setEditing(blankForm(me.today));
      const next = new URLSearchParams(params);
      next.delete('new');
      setParams(next, { replace: true });
    }
  }, [params, setParams, me.today]);

  const rows = q.data?.announcements ?? [];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select className="w-52" value={archived} onChange={(e) => setArchived(e.target.value as 'active')} aria-label={t('ann.archiveFilter')}>
          <option value="active">{t('ann.archiveModes.active')}</option>
          <option value="all">{t('ann.archiveModes.all')}</option>
          <option value="only">{t('ann.archiveModes.only')}</option>
        </Select>
        <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing(blankForm(me.today))}>
          {t('ann.newButton')}
        </Button>
      </div>
      <ErrorBox error={q.error ?? archive.error} />
      {q.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState>{t('ann.emptyManage')}</EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((r) => {
            const status = statusOf(r, me.today);
            return (
              <li key={r.id} className="rounded-lg border border-line bg-sheet px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <TypePill type={r.type} />
                      {r.priority !== 'normal' && <Pill tone={PRIORITY_TONE[r.priority]}>{t(`ann.priorities.${r.priority}`)}</Pill>}
                      {r.pinned && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-graphite">
                          <Pin className="size-3" /> {t('ann.pinned')}
                        </span>
                      )}
                      <Pill tone={STATUS_TONE[status]}>{t(`ann.status.${status}`)}</Pill>
                      {r.requiresAck && <Pill tone="accent">{t('ann.needsAck')}</Pill>}
                    </div>
                    <h3 className="mt-1 truncate text-[15px] font-semibold">{r.title}</h3>
                    <p className="mt-0.5 text-xs text-graphite">
                      {periodSummary(t, r)} · {audienceSummary(t, r.audience)}
                    </p>
                  </div>
                  <div className="num text-right text-sm">
                    <div className="font-semibold">{t('ann.readText', { read: r.readCount, reach: r.reach })}</div>
                    {r.requiresAck && <div className="text-xs text-graphite">{t('ann.ackText', { ack: r.ackCount })}</div>}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Button size="sm" icon={<Pencil className="size-3.5" />} onClick={() => setEditing(formFromRow(r))}>
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" icon={<Copy className="size-3.5" />} onClick={() => setEditing({ ...formFromRow(r), id: undefined, startsOn: me.today })}>
                    {t('ann.duplicate')}
                  </Button>
                  <Button size="sm" icon={<CheckCheck className="size-3.5" />} onClick={() => setReceiptsFor(r)}>
                    {t('ann.receipts')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={r.archived ? <ArchiveRestore className="size-3.5" /> : <Archive className="size-3.5" />}
                    loading={archive.isPending && archive.variables?.id === r.id}
                    onClick={() => archive.mutate({ id: r.id, archived: !r.archived }, { onSuccess: () => toast.ok(r.archived ? t('ann.restored') : t('ann.archivedToast')) })}
                  >
                    {r.archived ? t('common.unarchive') : t('common.archive')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {editing && <EditorDialog form={editing} onClose={() => setEditing(null)} />}
      {receiptsFor && <ReceiptsDialog row={receiptsFor} onClose={() => setReceiptsFor(null)} />}
    </div>
  );
}

type TFn = TFunction;

function recurrenceText(t: TFn, r: Recurrence): string {
  const unit = r.freq === 'weekly' ? 'weeks' : r.freq === 'monthly' ? 'months' : 'years';
  return r.interval === 1 ? t(`ann.every.${r.freq}`) : t('ann.everyN', { count: r.interval, unit: t(`ann.units.${unit}`) });
}

function periodSummary(t: TFn, r: ManageRow): string {
  if (r.recurrence) return `${t('ann.recurring')}: ${recurrenceText(t, r.recurrence)}${r.recurrence.until ? ` ${t('ann.untilShort', { date: d(r.recurrence.until) })}` : ''}`;
  const dates = r.endsOn ? `${d(r.startsOn)} – ${d(r.endsOn)}` : `${t('ann.fromShort', { date: d(r.startsOn) })}`;
  return r.eventDate ? `${dates} · ${t('ann.eventShort', { date: d(r.eventDate) })}` : dates;
}

function audienceSummary(t: TFn, a: Audience): string {
  if (a.all) return t('ann.audienceAll');
  const parts: string[] = [];
  for (const l of a.locations ?? []) parts.push(t('ann.locationOne', { code: l }));
  for (const r of a.roles ?? []) parts.push(roleLabel(t, r));
  if (a.projectIds?.length) parts.push(t('ann.projectsN', { count: a.projectIds.length }));
  return parts.join(', ') || '–';
}

function roleLabel(t: TFn, role: string): string {
  return role === 'lead' ? t('ann.roleLead') : t(`people.roles.${role as 'employee'}`, { defaultValue: role });
}

/* ------------------------------------------------------------------ */
/* Editor                                                               */
/* ------------------------------------------------------------------ */

interface Form {
  id?: string;
  type: AnnouncementType;
  title: string;
  body: string;
  priority: AnnouncementPriority;
  pinned: boolean;
  startsOn: string;
  endsOn: string;
  eventDate: string;
  eventTime: string;
  audienceAll: boolean;
  locations: string[];
  roles: string[];
  projectIds: string[];
  repeat: boolean;
  freq: Recurrence['freq'];
  interval: string;
  until: string;
  durationDays: string;
  leadDays: string;
  requiresAck: boolean;
  linkUrl: string;
  linkLabel: string;
}

function blankForm(today: string): Form {
  const def = typeDefaults('info');
  return {
    type: 'info',
    title: '',
    body: '',
    priority: def.priority,
    pinned: false,
    startsOn: today,
    endsOn: '',
    eventDate: '',
    eventTime: '',
    audienceAll: true,
    locations: [],
    roles: [],
    projectIds: [],
    repeat: false,
    freq: 'monthly',
    interval: '1',
    until: '',
    durationDays: '1',
    leadDays: '0',
    requiresAck: def.requiresAck,
    linkUrl: '',
    linkLabel: '',
  };
}

function formFromRow(r: ManageRow): Form {
  return {
    id: r.id,
    type: r.type,
    title: r.title,
    body: r.body,
    priority: r.priority,
    pinned: r.pinned,
    startsOn: r.startsOn,
    endsOn: r.endsOn ?? '',
    eventDate: r.eventDate ?? '',
    eventTime: r.eventTime ?? '',
    audienceAll: !!r.audience.all,
    locations: r.audience.locations ?? [],
    roles: r.audience.roles ?? [],
    projectIds: r.audience.projectIds ?? [],
    repeat: !!r.recurrence,
    freq: r.recurrence?.freq ?? 'monthly',
    interval: String(r.recurrence?.interval ?? 1),
    until: r.recurrence?.until ?? '',
    durationDays: String(r.recurrence?.durationDays ?? 1),
    leadDays: String(r.recurrence?.leadDays ?? 0),
    requiresAck: r.requiresAck,
    linkUrl: r.linkUrl ?? '',
    linkLabel: r.linkLabel ?? '',
  };
}

const ROLE_CHOICES = ['employee', 'lead', 'office', 'admin', 'external'] as const;

function EditorDialog({ form: initial, onClose }: { form: Form; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [f, setF] = useState<Form>(initial);
  const choices = useQ('announcements.audienceChoices', {});
  const save = useM('announcements.save', { invalidate: ['announcements.', 'home.', 'notifications.'] });
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  // a changed text of a published announcement asks the readers again (new version), unless it is only a correction
  const textChanged = !!f.id && (f.title !== initial.title || f.body !== initial.body || f.linkUrl !== initial.linkUrl || f.linkLabel !== initial.linkLabel);
  const [newRevision, setNewRevision] = useState(true);
  const dated = typeDefaults(f.type).dated;
  const audienceEmpty = !f.audienceAll && !f.locations.length && !f.roles.length && !f.projectIds.length;

  // choosing a type suggests priority and confirmation (the publisher can still change them)
  const setType = (type: AnnouncementType) => {
    const def = typeDefaults(type);
    setF((x) => ({ ...x, type, priority: def.priority, requiresAck: def.requiresAck }));
  };
  const toggle = (key: 'locations' | 'roles', v: string) => setF((x) => ({ ...x, [key]: x[key].includes(v) ? x[key].filter((y) => y !== v) : [...x[key], v] }));

  const projectName = useMemo(() => new Map((choices.data?.projects ?? []).map((p) => [p.id, `${p.number} ${p.name}`])), [choices.data]);

  const submit = () => {
    const recurrence = f.repeat
      ? {
          freq: f.freq,
          interval: Math.max(1, Number(f.interval) || 1),
          until: f.until || null,
          durationDays: Math.max(1, Number(f.durationDays) || 1),
          leadDays: Math.max(0, Number(f.leadDays) || 0),
        }
      : null;
    save.mutate(
      {
        id: f.id,
        type: f.type,
        title: f.title.trim(),
        body: f.body,
        priority: f.priority,
        pinned: f.pinned,
        startsOn: f.startsOn,
        endsOn: f.repeat ? null : f.endsOn || null,
        eventDate: dated ? f.eventDate || null : null,
        eventTime: dated && f.eventTime ? f.eventTime : null,
        audience: f.audienceAll ? { all: true } : { locations: f.locations, roles: f.roles, projectIds: f.projectIds },
        recurrence,
        requiresAck: f.requiresAck,
        linkUrl: f.linkUrl.trim() || null,
        linkLabel: f.linkLabel.trim() || null,
        ...(textChanged ? { newRevision } : {}),
      },
      {
        onSuccess: () => {
          toast.ok(f.id ? t('common.saved') : t('ann.published'));
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title={f.id ? t('ann.form.titleEdit') : t('ann.form.titleNew')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} disabled={!f.title.trim() || !f.startsOn || audienceEmpty} onClick={submit}>
            {f.id ? t('common.save') : t('ann.form.publish')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label={t('ann.form.type')} hint={t(`ann.typeHints.${f.type}`)} className="md:col-span-2">
          <Select value={f.type} onChange={(e) => setType(e.target.value as AnnouncementType)}>
            {ANNOUNCEMENT_TYPES.map((x) => (
              <option key={x} value={x}>
                {t(`ann.types.${x}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('ann.form.headline')} className="md:col-span-2">
          <Input value={f.title} maxLength={140} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label={t('ann.form.body')} className="md:col-span-2">
          <Textarea rows={5} maxLength={4000} value={f.body} onChange={(e) => set('body', e.target.value)} />
        </Field>

        <Field label={t('ann.form.priority')}>
          <Select value={f.priority} onChange={(e) => set('priority', e.target.value as AnnouncementPriority)}>
            {ANNOUNCEMENT_PRIORITIES.map((x) => (
              <option key={x} value={x}>
                {t(`ann.priorities.${x}`)}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-end pb-1.5">
          <Checkbox label={t('ann.form.pin')} hint={t('ann.form.pinHint')} checked={f.pinned} onChange={(e) => set('pinned', e.target.checked)} />
        </div>

        <Field label={t('ann.form.startsOn')}>
          <Input type="date" value={f.startsOn} onChange={(e) => set('startsOn', e.target.value)} />
        </Field>
        <Field label={t('ann.form.endsOn')} hint={f.repeat ? t('ann.form.endsOnRepeat') : t('ann.form.endsOnHint')}>
          <Input type="date" value={f.repeat ? '' : f.endsOn} min={f.startsOn} disabled={f.repeat} onChange={(e) => set('endsOn', e.target.value)} />
        </Field>
        {dated && (
          <>
            <Field label={t('ann.form.eventDate')}>
              <Input type="date" value={f.eventDate} onChange={(e) => set('eventDate', e.target.value)} />
            </Field>
            <Field label={t('ann.form.eventTime')}>
              <Input type="time" value={f.eventTime} onChange={(e) => set('eventTime', e.target.value)} />
            </Field>
          </>
        )}

        <div className="rounded-md border border-line p-3 md:col-span-2">
          <Checkbox label={t('ann.form.repeat')} hint={t('ann.form.repeatHint')} checked={f.repeat} onChange={(e) => set('repeat', e.target.checked)} />
          {f.repeat && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <Field label={t('ann.form.freq')}>
                <Select value={f.freq} onChange={(e) => set('freq', e.target.value as Recurrence['freq'])}>
                  {(['weekly', 'monthly', 'yearly'] as const).map((x) => (
                    <option key={x} value={x}>
                      {t(`ann.every.${x}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('ann.form.interval', { unit: t(`ann.units.${f.freq === 'weekly' ? 'weeks' : f.freq === 'monthly' ? 'months' : 'years'}`) })}>
                <Input type="number" min={1} max={52} value={f.interval} onChange={(e) => set('interval', e.target.value)} />
              </Field>
              <Field label={t('ann.form.until')}>
                <Input type="date" value={f.until} min={f.startsOn} onChange={(e) => set('until', e.target.value)} />
              </Field>
              <Field label={t('ann.form.duration')}>
                <Input type="number" min={1} max={60} value={f.durationDays} onChange={(e) => set('durationDays', e.target.value)} />
              </Field>
              <Field label={t('ann.form.lead')} hint={t('ann.form.leadHint')}>
                <Input type="number" min={0} max={60} value={f.leadDays} onChange={(e) => set('leadDays', e.target.value)} />
              </Field>
            </div>
          )}
        </div>

        <fieldset className="rounded-md border border-line p-3 md:col-span-2">
          <legend className="px-1 text-[13px] font-medium text-graphite">{t('ann.form.audience')}</legend>
          <div className="flex flex-col gap-3">
            <Checkbox label={t('ann.form.audienceAll')} hint={t('ann.form.audienceAllHint')} checked={f.audienceAll} onChange={(e) => set('audienceAll', e.target.checked)} />
            {!f.audienceAll && (
              <>
                <p className="text-xs text-graphite">{t('ann.form.audienceHint')}</p>
                <div>
                  <div className="mb-1 text-[13px] font-medium text-graphite">{t('ann.form.locations')}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {(choices.data?.locations ?? []).map((l) => (
                      <Checkbox key={l} label={l} checked={f.locations.includes(l)} onChange={() => toggle('locations', l)} />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-[13px] font-medium text-graphite">{t('ann.form.roles')}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {ROLE_CHOICES.map((r) => (
                      <Checkbox key={r} label={roleLabel(t, r)} checked={f.roles.includes(r)} onChange={() => toggle('roles', r)} />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-[13px] font-medium text-graphite">{t('ann.form.projects')}</div>
                  {f.projectIds.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {f.projectIds.map((id) => (
                        <Pill key={id} tone="accent">
                          {projectName.get(id) ?? id}
                          <button type="button" aria-label={t('common.delete')} onClick={() => set('projectIds', f.projectIds.filter((x) => x !== id))}>
                            <X className="size-3" />
                          </button>
                        </Pill>
                      ))}
                    </div>
                  )}
                  <Select
                    value=""
                    placeholder={t('ann.form.addProject')}
                    options={(choices.data?.projects ?? []).filter((p) => !f.projectIds.includes(p.id)).map((p) => ({ value: p.id, label: `${p.number} ${p.name}` }))}
                    onChange={(e) => e.target.value && set('projectIds', [...f.projectIds, e.target.value])}
                  />
                </div>
                <p className="text-xs text-graphite">{t('ann.form.externalHint')}</p>
              </>
            )}
            {audienceEmpty && <Notice tone="warn">{t('ann.form.needAudience')}</Notice>}
          </div>
        </fieldset>

        <div className="md:col-span-2">
          <Checkbox label={t('ann.form.ack')} hint={t('ann.form.ackHint')} checked={f.requiresAck} onChange={(e) => set('requiresAck', e.target.checked)} />
          {textChanged && (
            <Checkbox label={t('ann.form.newRevision')} hint={t('ann.form.newRevisionHint')} checked={newRevision} onChange={(e) => setNewRevision(e.target.checked)} />
          )}
        </div>
        <Field label={t('ann.form.linkUrl')} hint={t('ann.form.linkHint')}>
          <Input value={f.linkUrl} placeholder="https://" onChange={(e) => set('linkUrl', e.target.value)} />
        </Field>
        <Field label={t('ann.form.linkLabel')}>
          <Input value={f.linkLabel} maxLength={80} onChange={(e) => set('linkLabel', e.target.value)} />
        </Field>
      </div>
      <div className="mt-3">
        <ErrorBox error={save.error} />
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Read receipts                                                        */
/* ------------------------------------------------------------------ */

function ReceiptsDialog({ row, onClose }: { row: ManageRow; onClose: () => void }) {
  const { t } = useTranslation();
  const q = useQ('announcements.receipts', { id: row.id });
  const r = q.data;
  return (
    <Modal open onClose={onClose} title={t('ann.receiptsTitle')} footer={<Button onClick={onClose}>{t('common.close')}</Button>}>
      <div className="flex flex-col gap-3">
        <div>
          <div className="font-semibold">{row.title}</div>
          {r && (
            <div className="text-xs text-graphite">
              {t('ann.receiptsOccurrence', { date: d(r.occurrence) })}
              {r.revision > 1 && ` · ${t('ann.receiptsRevision', { revision: r.revision })}`}
            </div>
          )}
        </div>
        {q.isLoading && <Loading />}
        <ErrorBox error={q.error} />
        {r && (
          <>
            <Panel bodyClassName="grid grid-cols-3 gap-3 p-3">
              <Count label={t('ann.receiptsReach')} value={r.reach} />
              <Count label={t('ann.receiptsRead')} value={r.readCount} of={r.reach} />
              {r.requiresAck ? <Count label={t('ann.receiptsAcked')} value={r.ackCount} of={r.reach} /> : <div />}
            </Panel>
            {r.pending ? (
              r.pending.length ? (
                <div>
                  <div className="mb-1 text-[13px] font-medium text-graphite">{t('ann.receiptsPending', { count: r.pending.length })}</div>
                  <ul className="scroll-box columns-1 gap-4 text-sm sm:columns-2">
                    {r.pending.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <Notice tone="good">{t('ann.receiptsAllDone')}</Notice>
              )
            ) : (
              <p className="text-xs text-graphite">{t('ann.receiptsPrivacy')}</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function Count({ label, value, of }: { label: string; value: number; of?: number }) {
  return (
    <div>
      <div className="text-xs text-graphite">{label}</div>
      <div className="num text-xl font-semibold">
        {value}
        {of !== undefined && <span className="text-sm font-normal text-graphite"> / {of}</span>}
      </div>
    </div>
  );
}
